import { Platform } from 'react-native'

/**
 * Extracts MIME type and file extension from an image URI
 * Supports data URIs and file paths
 */
export const getMimeTypeAndExtension = (uri: string): { mimeType: string; extension: string } => {
  // Check if it's a data URI
  if (uri.startsWith('data:')) {
    const matches = uri.match(/data:([^;]+);base64,/)
    if (matches && matches[1]) {
      const mimeType = matches[1]
      const extension = mimeType.split('/')[1] || 'jpg'
      return { mimeType, extension }
    }
  }
  
  // Check file extension from URI
  const uriLower = uri.toLowerCase()
  if (uriLower.includes('.png')) {
    return { mimeType: 'image/png', extension: 'png' }
  }
  if (uriLower.includes('.jpg') || uriLower.includes('.jpeg')) {
    return { mimeType: 'image/jpeg', extension: 'jpg' }
  }
  if (uriLower.includes('.gif')) {
    return { mimeType: 'image/gif', extension: 'gif' }
  }
  if (uriLower.includes('.webp')) {
    return { mimeType: 'image/webp', extension: 'webp' }
  }
  
  // Default to JPEG
  return { mimeType: 'image/jpeg', extension: 'jpg' }
}

/**
 * Appends an avatar image to FormData with proper handling for web and native platforms
 * @param formData - The FormData instance to append to
 * @param avatarUri - The URI of the avatar image (data URI or file path)
 * @param fieldName - The field name to use in FormData (default: 'avatar')
 * @param fileName - Optional custom file name (default: 'avatar.{extension}')
 */
export const appendAvatarToFormData = (
  formData: FormData,
  avatarUri: string,
  fieldName: string = 'avatar',
  fileName?: string
): void => {
  // Extract MIME type and extension from avatar URI
  const { mimeType, extension } = getMimeTypeAndExtension(avatarUri)
  const avatarFileName = fileName || `avatar.${extension}`
  
  // Handle avatar differently for web vs native
  if (Platform.OS === 'web') {
    // Web: Handle data URI - convert to Blob
    if (avatarUri.startsWith('data:')) {
      // Extract base64 data from data URI
      const base64Data = avatarUri.split(',')[1]
      const blob = new Blob([Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))], { type: mimeType })
      formData.append(fieldName, blob, avatarFileName)
    } else {
      // Fallback for web if not data URI
      formData.append(fieldName, new Blob([avatarUri], { type: mimeType }), avatarFileName)
    }
  } else {
    // Native: Use object format that React Native FormData expects
    formData.append(fieldName, {
      uri: avatarUri,
      name: avatarFileName,
      type: mimeType,
    } as any)
  }
}

